/**
 * RCM control detail modal. Shared by the RCM library (row click) and the CUEC
 * card (click a mapped control, #75): code · description · process · owner ·
 * frequency · type · risk · last audit result · system.
 *
 * #127: the System tag is editable when an `onSaved` callback is provided (RCM
 * library); without it the modal stays read-only (the CUEC card's mapped-control
 * view).
 */
import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  FormField,
  Input,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Textarea,
} from "@cloudscape-design/components";
import type { RcmControl } from "@/core/api/types";
import { updateRcmControl } from "@/core/api/rcm";

export function RcmDetailModal({
  control,
  onDismiss,
  onSaved,
}: {
  control: RcmControl | null;
  onDismiss: () => void;
  /** #127: when provided, the System tag is editable and saved via PATCH. */
  onSaved?: (updated: RcmControl) => void;
}) {
  const [system, setSystem] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSystem(control?.system ?? "");
    setError(null);
  }, [control]);

  const editable = !!onSaved;

  const saveSystem = async () => {
    if (!control) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await updateRcmControl(control.id, system.trim() || null);
      onSaved?.(updated);
      onDismiss();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible={control !== null}
      onDismiss={onDismiss}
      header={control ? `${control.code} — ${control.name}` : ""}
      size="medium"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant={editable ? "link" : "primary"} onClick={onDismiss} disabled={busy}>
              {editable ? "Cancel" : "Close"}
            </Button>
            {editable && (
              <Button variant="primary" onClick={saveSystem} loading={busy} disabled={busy}>
                Save
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      {control && (
        <SpaceBetween size="m">
          {error && (
            <Alert type="error" header="Save failed">
              {error}
            </Alert>
          )}
          <FormField label="Description">
            <Textarea value={control.description ?? "—"} rows={3} readOnly />
          </FormField>
          <ColumnLayout columns={2}>
            <FormField label="Process">
              <Input value={control.process ?? "—"} readOnly />
            </FormField>
            <FormField label="Owner">
              <Input value={control.owner ?? "—"} readOnly />
            </FormField>
            <FormField label="Frequency">
              <Input value={control.frequency ?? "—"} readOnly />
            </FormField>
            <FormField label="Type">
              <Input value={control.control_type ?? "—"} readOnly />
            </FormField>
          </ColumnLayout>
          <FormField
            label="System"
            description={
              editable
                ? "The in-scope system this control applies to (e.g. Boomi, Oracle Fusion). Used to match vendor-specific CUECs."
                : undefined
            }
          >
            {editable ? (
              <Input
                value={system}
                onChange={({ detail }) => setSystem(detail.value)}
                placeholder="e.g. Boomi"
                disabled={busy}
              />
            ) : (
              <Input value={control.system ?? "—"} readOnly />
            )}
          </FormField>
          <FormField label="Risk">
            <Textarea value={control.risk ?? "—"} rows={3} readOnly />
          </FormField>
          <FormField label="Last audit result">
            {control.last_audit_result === "Fail" ? (
              <StatusIndicator type="error">Fail</StatusIndicator>
            ) : control.last_audit_result === "Pass" ? (
              <StatusIndicator type="success">Pass</StatusIndicator>
            ) : (
              <StatusIndicator type="pending">Unknown</StatusIndicator>
            )}
          </FormField>
        </SpaceBetween>
      )}
    </Modal>
  );
}
